import type {BrandKitPill, NewFolderGridIndexParams, SavedFolderGridIndexParams, UploadsItemsRequest, UploadsListing, UploadsPill, UploadsTabState} from '@Panels/user-media-panel/user-media-panel.types';
import type {TabKey} from '@Components/tabs/tabs.types';
import {
  BRAND_KIT_ASSETS_GRID_ID,
  BRAND_KIT_SUB_TABS,
  NEW_COLLECTION_BUTTON_GRID_ITEM_ID,
  UploadsItemsEndpoint,
  UploadsListSource,
  UploadsMediaSubTab,
  UploadsMediaType,
  UploadsSortOrder,
  UploadsSourceGroup,
  USER_AI_GENERATED_AUDIO_GRID_ID,
  USER_AI_GENERATED_IMAGE_GRID_ID,
  USER_AI_GENERATED_VIDEO_GRID_ID,
  USER_AUDIO_UPLOADED_ONLY_GRID_ID,
  USER_AUDIO_UPLOADS_GRID_ID,
  USER_IMAGE_UPLOADED_ONLY_GRID_ID,
  USER_IMAGE_UPLOADS_GRID_ID,
  USER_LOGOS_GRID_ID,
  USER_VIDEO_UPLOADED_ONLY_GRID_ID,
  USER_VIDEO_UPLOADS_GRID_ID,
  UserMediaPanelAiOption,
} from '@Panels/user-media-panel/user-media-panel.types';
import type {ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE, ControlledListItemSize} from '@Components/controlled-list/controlled-list.types';
import type {GridButtonItemStorage} from '@Components/base-grid/components/grid-button-item';
import type {GridCollectionItemStorage} from '@Components/base-grid/components/grid-collection-item';
import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import {GRID_ITEM_TYPE} from '@Components/base-grid/components/grid-item';
import {FLEX_DIRECTION} from '@Utils/dom.util';
import {IconSize} from '@Components/icon-v2/icon.types';
import {TextSize} from '@Components/text';
import type {LazyLoadingGetterParams, LazyLoadingOffsets} from '@Libraries/pmw-stock-media-library';
import {assertNever} from '@Utils/assert-never.util';
import {isAlphabeticalUploadsSortOrder} from '@Panels/user-media-panel/uploads-sort-order.library';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events, UploadsPillGA4Type} from '@Libraries/ga-events';
import {isBrandKitLogoGridItem} from '@Libraries/user-media-library';
import {UserImageSource} from '@Libraries/user-image-library';
import {UserVideoSource} from '@Libraries/user-video-library';
import {UserAudioSource} from '@Libraries/user-audio-library';

/**
 * The AI Images view's StockPanel registers its panelId in the panel registry and deregisters it on
 * unmount — so it must not share the uploads panel's id, or going Back would deregister the host and
 * every later close would throw "Panel not initialized".
 */
export const getUploadsAiImagesPanelId = (uploadsPanelId: string): string => {
  return `${uploadsPanelId}-uploads-ai-images`;
};

/**
 * On poster pages, the editor above all, message growls render under every modal, so the uploads panel shows them
 * itself. Pages whose own growl host sits above modals (My Stuff, the email and event wizards) keep theirs, so no
 * growl shows twice. Only poster pages load the whiteboard that defines window.posterEditor.
 */
export const shouldUploadsPanelShowMessageGrowls = (): boolean => {
  return window.posterEditor !== undefined;
};

/** The background pickers' tabs: what they list is set as a background, and their items can't be filed into folders. */
export const isBackgroundUploadsTab = (tab: UploadsMediaType): boolean => {
  return tab === UploadsMediaType.IMAGE_BACKGROUND || tab === UploadsMediaType.VIDEO_BACKGROUND;
};

export const getGridIdForTabStates = (tab: UploadsMediaType, subTab: UploadsMediaSubTab): string => {
  switch (tab) {
    case UploadsMediaType.IMAGE:
    case UploadsMediaType.IMAGE_BACKGROUND:
      if (subTab === UploadsMediaSubTab.AI_IMAGE) {
        return USER_AI_GENERATED_IMAGE_GRID_ID;
      }
      if (subTab === UploadsMediaSubTab.LOGO) {
        return USER_LOGOS_GRID_ID;
      }
      return subTab === UploadsMediaSubTab.UPLOADS ? USER_IMAGE_UPLOADED_ONLY_GRID_ID : USER_IMAGE_UPLOADS_GRID_ID;
    case UploadsMediaType.AUDIO:
      if (subTab === UploadsMediaSubTab.AI_VOICE) {
        return USER_AI_GENERATED_AUDIO_GRID_ID;
      }
      return subTab === UploadsMediaSubTab.UPLOADS ? USER_AUDIO_UPLOADED_ONLY_GRID_ID : USER_AUDIO_UPLOADS_GRID_ID;
    case UploadsMediaType.VIDEO:
    case UploadsMediaType.VIDEO_BACKGROUND:
      if (subTab === UploadsMediaSubTab.AI_VIDEO) {
        return USER_AI_GENERATED_VIDEO_GRID_ID;
      }
      return subTab === UploadsMediaSubTab.UPLOADS ? USER_VIDEO_UPLOADED_ONLY_GRID_ID : USER_VIDEO_UPLOADS_GRID_ID;
    case UploadsMediaType.BRAND_KIT:
      return BRAND_KIT_ASSETS_GRID_ID;
    default:
      throw new Error(`Unhandled tab: ${tab} with subtab: ${subTab}`);
  }
};

/* The tabs store keeps plain keys; these narrow the uploads panel's own keys back to their enums without a cast. */
const UPLOADS_MEDIA_TYPE_KEYS = Object.values<string>(UploadsMediaType);
const UPLOADS_MEDIA_SUB_TAB_KEYS = Object.values<string>(UploadsMediaSubTab);

export const isUploadsMediaType = (tabKey: TabKey): tabKey is UploadsMediaType => {
  return typeof tabKey === 'string' && UPLOADS_MEDIA_TYPE_KEYS.includes(tabKey);
};

export const isUploadsMediaSubTab = (tabKey: TabKey): tabKey is UploadsMediaSubTab => {
  return typeof tabKey === 'string' && UPLOADS_MEDIA_SUB_TAB_KEYS.includes(tabKey);
};

export const isBrandKitSubTab = (subTab: UploadsMediaSubTab): subTab is BrandKitPill => {
  return BRAND_KIT_SUB_TABS.some((pill) => {
    return pill === subTab;
  });
};

/** The call that fetches one page of the items grid for the listing the panel shows. */
export const getUploadsItemsRequest = (listing: UploadsListing, page: LazyLoadingGetterParams): UploadsItemsRequest => {
  const endpoint = getUploadsItemsEndpoint(listing);
  const offset = getTotalOffset(page.offsets);
  switch (endpoint) {
    case UploadsItemsEndpoint.AI_VIDEOS:
      return {endpoint, params: {offset, limit: page.limit, sortBy: listing.sortOrder}};
    case UploadsItemsEndpoint.AI_IMAGES:
    case UploadsItemsEndpoint.AI_VOICES:
      return {endpoint, params: {o: offset, l: page.limit, sortBy: listing.sortOrder}};
    case UploadsItemsEndpoint.COLLECTION_ITEMS: {
      const brandLogosOffset = page.offsets[UploadsListSource.BRAND_LOGOS] ?? 0;
      return {
        endpoint,
        params: {
          o: offset - brandLogosOffset,
          l: page.limit,
          sortBy: listing.sortOrder,
          c: listing.collectionId,
          collectionType: listing.tab === UploadsMediaType.VIDEO_BACKGROUND ? UploadsMediaType.VIDEO : listing.tab,
          sources: getUploadsSourceGroup(listing.subTab),
          lo: brandLogosOffset,
        },
      };
    }
    default:
      return assertNever(endpoint);
  }
};

const getUploadsItemsEndpoint = ({tab, subTab}: UploadsTabState): UploadsItemsEndpoint => {
  const isImageTab = tab === UploadsMediaType.IMAGE || tab === UploadsMediaType.IMAGE_BACKGROUND;
  const isVideoTab = tab === UploadsMediaType.VIDEO || tab === UploadsMediaType.VIDEO_BACKGROUND;
  if (isImageTab && subTab === UploadsMediaSubTab.AI_IMAGE) {
    return UploadsItemsEndpoint.AI_IMAGES;
  }
  if (isVideoTab && subTab === UploadsMediaSubTab.AI_VIDEO) {
    return UploadsItemsEndpoint.AI_VIDEOS;
  }
  if (tab === UploadsMediaType.AUDIO && subTab === UploadsMediaSubTab.AI_VOICE) {
    return UploadsItemsEndpoint.AI_VOICES;
  }
  return UploadsItemsEndpoint.COLLECTION_ITEMS;
};

/** The uploads listings page through one source, so the grid's per-source offsets sum to the listing's offset. */
const getTotalOffset = (offsets: LazyLoadingOffsets): number => {
  return Object.values(offsets).reduce((total, offset) => {
    return total + offset;
  }, 0);
};

const getUploadsSourceGroup = (subTab: UploadsMediaSubTab): UploadsSourceGroup => {
  if (subTab === UploadsMediaSubTab.LOGO) {
    return UploadsSourceGroup.BRAND_LOGOS_ONLY;
  }
  return subTab === UploadsMediaSubTab.UPLOADS ? UploadsSourceGroup.UPLOADED_ONLY : UploadsSourceGroup.ALL;
};

/**
 * Skeleton rows count as folders: the folders grid holds them while a reload (a sort change, a tab
 * switch) is in flight, and UI that hides or resets folder-only options must not react to that gap.
 */
export const hasFoldersOrIsLoadingFolders = (folderGridItems: GridItemStorage[] | undefined): boolean => {
  return (
    folderGridItems?.some((item) => {
      return item.type === GRID_ITEM_TYPE.COLLECTION || item.type === GRID_ITEM_TYPE.SKELETON;
    }) ?? false
  );
};

/** A grid whose items have arrived and are none; a grid still loading has no items list yet. */
export const isGridLoadedAndEmpty = (gridItems: GridItemStorage[] | undefined): boolean => {
  return gridItems?.length === 0;
};

/** Neither a folder (loading ones count) nor a listed item: the uploads area has nothing to show or sort. */
export const isUploadsListingEmpty = (folderGridItems: GridItemStorage[] | undefined, activeGridItems: GridItemStorage[] | undefined): boolean => {
  return !hasFoldersOrIsLoadingFolders(folderGridItems) && isGridLoadedAndEmpty(activeGridItems);
};

/** The folders grid also lists the create-folder tile and, while loading, skeletons; this picks out the folder tiles. */
export const isFolderGridItem = (gridItem: GridItemStorage): gridItem is GridCollectionItemStorage => {
  return gridItem.type === GRID_ITEM_TYPE.COLLECTION;
};

export const getFolderGridItemNames = (folderGridItems: GridItemStorage[] | undefined): string[] => {
  return (folderGridItems ?? []).filter(isFolderGridItem).map((folderGridItem) => {
    return folderGridItem.collectionName;
  });
};

/** The name on the folder tile whose grid item id is `folderGridItemId`; empty when the folders grid does not list it. */
export const getFolderGridItemName = (folderGridItems: GridItemStorage[] | undefined, folderGridItemId: string): string => {
  const folderGridItem = folderGridItems?.filter(isFolderGridItem).find((gridItem) => {
    return gridItem.id === folderGridItemId;
  });
  return folderGridItem?.collectionName ?? '';
};

/**
 * Where the active sort order lists a new folder tile in the folders grid, so a folder created anywhere in the
 * panel shows up without a reload; undefined appends it at the end. The server lists the user's own folders
 * before the shared ones, so a new folder never goes past the first shared one.
 */
export const getNewFolderGridIndex = ({folderGridItems, sortOrder, newFolderName, leadingGridItemsCount}: NewFolderGridIndexParams): number | undefined => {
  const getIndexOfFirstFolder = (isMatchingFolder: (folderGridItem: GridCollectionItemStorage) => boolean): number | undefined => {
    const folderIndex = (folderGridItems ?? []).findIndex((gridItem) => {
      return isFolderGridItem(gridItem) && isMatchingFolder(gridItem);
    });
    return folderIndex === -1 ? undefined : folderIndex;
  };

  const doesNewNameComeFirst = (existingName: string): boolean => {
    const nameComparison = newFolderName.localeCompare(existingName, undefined, {sensitivity: 'base'});
    return sortOrder === UploadsSortOrder.ALPHABETICAL_A_TO_Z ? nameComparison < 0 : nameComparison > 0;
  };

  if (sortOrder === UploadsSortOrder.OLDEST_FIRST) {
    // A just-created folder is the newest, so it is the last of the user's own folders.
    return getIndexOfFirstFolder((folderGridItem) => {
      return folderGridItem.isShared;
    });
  }
  if (isAlphabeticalUploadsSortOrder(sortOrder)) {
    return getIndexOfFirstFolder((folderGridItem) => {
      return folderGridItem.isShared || doesNewNameComeFirst(folderGridItem.collectionName);
    });
  }
  return leadingGridItemsCount;
};

/**
 * Where a folder tile that is already in the grid belongs once its name is final, counted among the other tiles
 * (the tile itself is taken out first, which is how moveGridItem counts too). The tiles ahead of the first
 * remaining folder are the leading ones. Undefined moves it to the end.
 */
export const getSavedFolderGridIndex = ({folderGridItems, folderId, folderName, sortOrder}: SavedFolderGridIndexParams): number | undefined => {
  const otherGridItems = (folderGridItems ?? []).filter((gridItem) => {
    return gridItem.id !== folderId;
  });
  const firstFolderIndex = otherGridItems.findIndex(isFolderGridItem);
  return getNewFolderGridIndex({
    folderGridItems: otherGridItems,
    sortOrder,
    newFolderName: folderName,
    leadingGridItemsCount: firstFolderIndex === -1 ? otherGridItems.length : firstFolderIndex,
  });
};

export const getNewCollectionButtonGridItem = (): GridButtonItemStorage => {
  return {
    id: NEW_COLLECTION_BUTTON_GRID_ITEM_ID,
    type: GRID_ITEM_TYPE.BUTTON,
    text: window.i18next.t('pmwjs_create_a_folder'),
    icon: 'icon-new-folder',
    className: '-dotted-icon-button-rounded -folder-grid-button',
    flexDirection: FLEX_DIRECTION.ROW,
    iconSize: IconSize.SIZE_ICON_24,
    textSize: TextSize.MD,
  };
};

export const getItemsforAiOptionsDropdown = (mediaTypesToShow: UploadsMediaType[]): ControlledListItemType[] => {
  const items: ControlledListItemType[] = [];

  if (mediaTypesToShow.includes(UploadsMediaType.IMAGE) || mediaTypesToShow.includes(UploadsMediaType.IMAGE)) {
    items.push({
      id: UserMediaPanelAiOption.AI_IMAGE,
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      text: window.i18next.t('pmwjs_ai_image'),
      icon: 'icon-ai-image',
      listSize: ControlledListItemSize.MEDIUM,
    });
  }
  if (mediaTypesToShow.includes(UploadsMediaType.AUDIO)) {
    items.push({
      id: UserMediaPanelAiOption.AI_VOICE,
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      text: window.i18next.t('pmwjs_ai_voice'),
      icon: 'icon-ai-voice',
      listSize: ControlledListItemSize.MEDIUM,
    });
  }

  return items;
};

/**
 * The add event for one file added from the uploads panel. Brand Kit logos and each kind of AI creation report their
 * own event, so the photo, video and audio adds count the user's own uploads only.
 */
export const getUploadsAddGA4EventName = (gridItem: GridItemStorage): GA4EventName => {
  if (isBrandKitLogoGridItem(gridItem)) {
    return GA4EventName.UPLOADS_MODAL_LOGO_ADD;
  }
  if (isAiImageGridItem(gridItem)) {
    return GA4EventName.UPLOADS_MODAL_AI_IMAGE_ADD;
  }
  if (isAiVideoClipGridItem(gridItem)) {
    return GA4EventName.UPLOADS_MODAL_AI_VIDEO_ADD;
  }
  if (isAiPosterAnimationGridItem(gridItem)) {
    return GA4EventName.UPLOADS_MODAL_AI_ANIMATION_ADD;
  }
  if (isAiVoiceGridItem(gridItem)) {
    return GA4EventName.UPLOADS_MODAL_AI_VOICE_ADD;
  }
  return getUploadedFileAddGA4EventName(gridItem.type);
};

/** Reports a switch to another filter pill; picking the pill already shown sends nothing. */
export const trackUploadsPillSwitch = (pickedSubTab: UploadsPill, activeSubTab: UploadsMediaSubTab): void => {
  if (pickedSubTab === activeSubTab) {
    return;
  }
  trackPosterBuilderGA4Events(GA4EventName.UPLOADS_MODAL_PILL_SWITCH, {[GA4EventParamName.TYPE]: UPLOADS_PILL_GA4_TYPES[pickedSubTab]});
};

export const isAiImageGridItem = (gridItem: GridItemStorage): boolean => {
  return gridItem.source === UserImageSource.AI_TEXT_TO_IMAGE;
};

/** AI videos and AI poster animations: the video tab's AI Creations pill lists both. */
export const isAiVideoGridItem = (gridItem: GridItemStorage): boolean => {
  return isAiVideoClipGridItem(gridItem) || isAiPosterAnimationGridItem(gridItem);
};

export const isAiVoiceGridItem = (gridItem: GridItemStorage): boolean => {
  return gridItem.source === UserAudioSource.TEXT_TO_SPEECH;
};

const isAiVideoClipGridItem = (gridItem: GridItemStorage): boolean => {
  return gridItem.source === UserVideoSource.AI_VIDEO;
};

const isAiPosterAnimationGridItem = (gridItem: GridItemStorage): boolean => {
  return gridItem.source === UserVideoSource.AI_POSTER_ANIMATION;
};

/** Anything that is not a video or an audio file has always counted as a photo add. */
const getUploadedFileAddGA4EventName = (gridItemType: GRID_ITEM_TYPE): GA4EventName => {
  if (gridItemType === GRID_ITEM_TYPE.USER_VIDEO) {
    return GA4EventName.UPLOADS_MODAL_VIDEO_ADD;
  }
  if (gridItemType === GRID_ITEM_TYPE.USER_AUDIO) {
    return GA4EventName.UPLOADS_MODAL_AUDIO_ADD;
  }
  return GA4EventName.UPLOADS_MODAL_PHOTO_ADD;
};

const UPLOADS_PILL_GA4_TYPES: Record<UploadsPill, UploadsPillGA4Type> = {
  [UploadsMediaSubTab.ALL]: UploadsPillGA4Type.ALL,
  [UploadsMediaSubTab.UPLOADS]: UploadsPillGA4Type.UPLOADED,
  [UploadsMediaSubTab.LOGO]: UploadsPillGA4Type.LOGOS,
  [UploadsMediaSubTab.AI_IMAGE]: UploadsPillGA4Type.AI_IMAGE,
  [UploadsMediaSubTab.AI_VIDEO]: UploadsPillGA4Type.AI_VIDEO,
  [UploadsMediaSubTab.AI_VOICE]: UploadsPillGA4Type.AI_VOICE,
};
